// Make slider initialization a reusable function
function initFeaturedSlider(slider) {
  // Guard: never bind the same element twice (e.g. multiple init callers).
  if (slider.dataset.fsInit === "true") return;
  slider.dataset.fsInit = "true";

  const container = slider.parentElement;
  const header = container.closest(
    ".featured-container"
  )?.previousElementSibling;
  const prevBtn = header?.querySelector(".prev");
  const nextBtn = header?.querySelector(".next");
  const items = slider.querySelectorAll(".product-card-item");

  if (!items.length) return;

  // Helper functions
  const getCurrentGap = () =>
    parseInt(window.getComputedStyle(slider).gap) || 10;
  const getCardWidth = () => items[0].offsetWidth + getCurrentGap();

  // Slider state
  let currentPosition = 0;
  let maxPosition = slider.scrollWidth - container.offsetWidth;
  let isDragging = false;
  let startX = 0;
  let startY = 0;
  let dragAxis = null; // 'x' once locked horizontal, 'y' released to page scroll
  let startPosition = 0;
  const clickThreshold = 5;
  const swipeThreshold = 50; // px dragged past which we advance to the next card

  // Initialize
  updateButtons();

  // 1. PREVENT IMAGE DRAGGING
  slider.querySelectorAll("img").forEach((img) => {
    img.setAttribute("draggable", "false");
    img.addEventListener("dragstart", (e) => {
      e.preventDefault();
      return false;
    });
  });

  // 2. NAVIGATION BUTTONS
  if (prevBtn && nextBtn) {
    prevBtn.addEventListener("click", () => {
      currentPosition = Math.min(currentPosition + getCardWidth(), 0);
      slideTo(currentPosition);
    });

    nextBtn.addEventListener("click", () => {
      currentPosition = Math.max(
        currentPosition - getCardWidth(),
        -maxPosition
      );
      slideTo(currentPosition);
    });
  }

  // 3. DRAG HANDLING - MOUSE AND TOUCH
  slider.addEventListener("mousedown", startDrag);
  slider.addEventListener("touchstart", startDrag, { passive: true });

  function removeDragListeners() {
    document.removeEventListener("mousemove", handleDrag);
    document.removeEventListener("touchmove", handleDrag);
    document.removeEventListener("mouseup", endDrag);
    document.removeEventListener("touchend", endDrag);
  }

  function startDrag(e) {
    if (e.type === "mousedown" && e.button !== 0) return;

    const isTouch = e.type === "touchstart";
    isDragging = false;
    dragAxis = isTouch ? null : "x"; // mouse has no scroll conflict
    startX = isTouch ? e.touches[0].clientX : e.clientX;
    startY = isTouch ? e.touches[0].clientY : e.clientY;
    startPosition = currentPosition;

    document.addEventListener("mousemove", handleDrag);
    document.addEventListener("touchmove", handleDrag, { passive: false });
    document.addEventListener("mouseup", endDrag);
    document.addEventListener("touchend", endDrag);

    // Only block defaults for mouse (text selection / native image drag).
    // Blocking touchstart would kill vertical page scrolling on mobile.
    if (!isTouch) e.preventDefault();
  }

  function handleDrag(e) {
    const isTouch = e.type === "touchmove";
    const currentX = isTouch ? e.touches[0].clientX : e.clientX;
    const currentY = isTouch ? e.touches[0].clientY : e.clientY;
    const diff = startX - currentX;

    // Axis lock: decide once, from the first meaningful movement.
    if (!dragAxis) {
      const diffY = startY - currentY;
      if (Math.abs(diff) < clickThreshold && Math.abs(diffY) < clickThreshold) return;
      if (Math.abs(diffY) > Math.abs(diff)) {
        // Vertical intent: release the gesture so the page scrolls natively.
        dragAxis = "y";
        removeDragListeners();
        return;
      }
      dragAxis = "x";
    }

    if (!isDragging && Math.abs(diff) < clickThreshold) return;

    if (!isDragging) {
      isDragging = true;
      slider.classList.add("dragging");
      document.body.classList.add("slider-dragging");
      slider.style.transition = "none";
    }

    currentPosition = Math.max(Math.min(startPosition - diff, 0), -maxPosition);
    slider.style.transform = `translateX(${currentPosition}px)`;

    e.preventDefault();
  }

  function suppressClickAfterDrag(ev) {
    ev.preventDefault();
    ev.stopPropagation();
  }

  function endDrag(e) {
    removeDragListeners();

    document.body.classList.remove("slider-dragging");

    if (!isDragging) {
      // Touch taps produce a native click (touchstart is no longer prevented);
      // only mouse needs the manual click-through.
      if (e.type === "mouseup") {
        const target = e.target.closest("a");
        if (target) target.click();
      }
      return;
    }

    isDragging = false;
    slider.classList.remove("dragging");

    // Swallow the stray click that can follow a real drag.
    slider.addEventListener("click", suppressClickAfterDrag, true);
    setTimeout(function () {
      slider.removeEventListener("click", suppressClickAfterDrag, true);
    }, 50);

    // Small-swipe threshold: past `swipeThreshold` px always advances at least
    // one card in the drag direction; below it, snap back.
    const cardWidth = getCardWidth();
    const dragDistance = startPosition - currentPosition; // >0 = dragged towards next
    const startIndex = Math.round(Math.abs(startPosition) / cardWidth);
    let cardIndex;
    if (Math.abs(dragDistance) >= swipeThreshold) {
      const cardsMoved = Math.max(1, Math.round(Math.abs(dragDistance) / cardWidth));
      cardIndex = dragDistance > 0 ? startIndex + cardsMoved : startIndex - cardsMoved;
    } else {
      cardIndex = startIndex;
    }
    cardIndex = Math.max(0, cardIndex);
    currentPosition = -Math.min(cardIndex * cardWidth, maxPosition);
    slideTo(currentPosition);
  }

  function slideTo(position) {
    currentPosition = position;
    slider.style.transition = "transform 0.3s ease";
    slider.style.transform = `translateX(${position}px)`;
    updateButtons();
  }

  function updateButtons() {
    if (!prevBtn || !nextBtn) return;
    prevBtn.disabled = currentPosition >= 0;
    nextBtn.disabled = currentPosition <= -maxPosition || maxPosition <= 0;
  }

  // Handle responsive changes
  const resizeObserver = new ResizeObserver(() => {
    maxPosition = slider.scrollWidth - container.offsetWidth;
    currentPosition = Math.max(Math.min(currentPosition, 0), -maxPosition);
    slideTo(currentPosition);
    updateButtons();
  });
  resizeObserver.observe(container);
}

// Initialize all sliders on page load
document.addEventListener("DOMContentLoaded", function () {
  document.querySelectorAll(".featured-slider").forEach(initFeaturedSlider);
});

// Make the function available globally for reinitialization
window.initFeaturedSlider = initFeaturedSlider;
